import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useWallet } from '../contexts/WalletContext';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface UserStats {
  totalPredictions: number;
  correctPredictions: number;
  ringerScore: number;
  currentStreak: number;
  longestStreak: number;
  rank: number;
}

const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const { balance } = useWallet();
  const [stats, setStats] = useState<UserStats>({
    totalPredictions: 0,
    correctPredictions: 0,
    ringerScore: 0,
    currentStreak: 0,
    longestStreak: 0,
    rank: 0
  });
  const [loading, setLoading] = useState(true);

  const loadUserStats = useCallback(async () => {
    if (!currentUser) return;

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        setStats(userDoc.data() as UserStats);
      } else {
        // Initialize new user stats
        const initialStats: UserStats = {
          totalPredictions: 0,
          correctPredictions: 0,
          ringerScore: 0,
          currentStreak: 0,
          longestStreak: 0,
          rank: 0
        };
        await setDoc(userDocRef, initialStats);
        setStats(initialStats);
      }
    } catch (error) {
      console.error('Error loading user stats:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadUserStats();
  }, [loadUserStats]);

  const accuracy = stats.totalPredictions > 0 
    ? ((stats.correctPredictions / stats.totalPredictions) * 100).toFixed(1)
    : '0.0';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Dashboard</h1>
        <div className="text-right">
          <p className="text-sm text-gray-600">Welcome back!</p>
          <p className="text-lg font-semibold">{currentUser?.email}</p>
        </div>
      </div>

      {/* Ringer$ Balance Card */}
      <div className="card bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-90">Your Balance</p>
            <h2 className="text-4xl font-bold">{balance.toLocaleString()} Ringer$</h2>
          </div>
          <div className="text-6xl">💰</div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Ringer Score</h3>
            <span className="text-2xl">🎯</span>
          </div>
          <p className="text-3xl font-bold text-primary-600">{stats.ringerScore}</p>
          <p className="text-sm text-gray-600 mt-1">Your overall rating</p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Accuracy</h3>
            <span className="text-2xl">📊</span>
          </div>
          <p className="text-3xl font-bold text-primary-600">{accuracy}%</p>
          <p className="text-sm text-gray-600 mt-1">
            {stats.correctPredictions} of {stats.totalPredictions} correct
          </p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Current Streak</h3>
            <span className="text-2xl">🔥</span>
          </div>
          <p className="text-3xl font-bold text-primary-600">{stats.currentStreak}</p>
          <p className="text-sm text-gray-600 mt-1">
            Best: {stats.longestStreak}
          </p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Global Rank</h3>
            <span className="text-2xl">🏆</span>
          </div>
          <p className="text-3xl font-bold text-primary-600">
            #{stats.rank || 'Unranked'}
          </p>
          <p className="text-sm text-gray-600 mt-1">Among all Ringers</p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Total Predictions</h3>
            <span className="text-2xl">📈</span>
          </div>
          <p className="text-3xl font-bold text-primary-600">{stats.totalPredictions}</p>
          <p className="text-sm text-gray-600 mt-1">All time</p>
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-700">Earnings</h3>
            <span className="text-2xl">💵</span>
          </div>
          <p className="text-3xl font-bold text-primary-600">{balance}</p>
          <p className="text-sm text-gray-600 mt-1">Ringer$ earned</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card">
        <h3 className="text-xl font-bold mb-4">Quick Actions</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <button className="btn-primary">
            Browse Auctions
          </button>
          <button className="btn-secondary">
            View My Predictions
          </button>
          <button className="btn-secondary">
            Check Leaderboard
          </button>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h3 className="text-xl font-bold mb-4">Recent Activity</h3>
        <p className="text-gray-600 text-center py-8">
          No recent activity. Start making predictions to see your activity here!
        </p>
      </div>
    </div>
  );
};

export default Dashboard;